/**
 * 위젯 v2 기간 계산 (2026-09-16 · 대시보드 시안 v2 V3a · 순수 함수).
 *
 * ★ 주는 **월요일부터**다 — 주간 타임라인 · 이번 주 요약과 같은 셈.
 * ★ 범위는 [from, to) — 끝은 포함하지 않는다.
 */

export interface Range { from: Date; to: Date }

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function weekStart(d: Date): Date {
  const s = startOfDay(d);
  return addDays(s, -((s.getDay() + 6) % 7));
}

export function periodRange(period: 'month' | 'week', now: Date): Range {
  if (period === 'week') {
    const from = weekStart(now);
    return { from, to: addDays(from, 7) };
  }
  return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 1) };
}

export function previousRange(period: 'month' | 'week', r: Range): Range {
  if (period === 'week') return { from: addDays(r.from, -7), to: r.from };
  return { from: new Date(r.from.getFullYear(), r.from.getMonth() - 1, 1), to: r.from };
}

export function inRange(at: string | Date | null | undefined, r: Range): boolean {
  if (!at) return false;
  const t = new Date(at).getTime();
  return Number.isFinite(t) && t >= r.from.getTime() && t < r.to.getTime();
}

/** 이 범위가 그 달(0-기준 월)과 겹치나 — 옆 달 일정을 받을지 정한다. */
export function touchesMonth(r: Range, year: number, month0: number): boolean {
  const a = new Date(year, month0, 1);
  const b = new Date(year, month0 + 1, 1);
  return r.from < b && r.to > a;
}

/** 종일이 아닌 일정의 분 — 종일은 0(KPI 「일정 시간」 · 옛 위젯과 같은 셈). */
export function eventMinutes(e: { startAt: string; endAt: string; isAllDay?: boolean }): number {
  if (e.isAllDay) return 0;
  return Math.max(0, (new Date(e.endAt).getTime() - new Date(e.startAt).getTime()) / 60000);
}

export function mmdd(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

/** 비교 배지 — 「+2」 · 「−0.5h」 · 「±0」. */
export function signed(n: number, digits = 0, unit = ''): string {
  const v = Number(n.toFixed(digits));
  if (v === 0) return `±0${unit}`;
  return `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}${unit}`;
}

export function uniqueById<T extends { id: string }>(rows: readonly T[]): T[] {
  const m = new Map<string, T>();
  for (const r of rows) m.set(r.id, r);
  return [...m.values()];
}
